import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, Globe, BarChart3, ArrowRight, ExternalLink } from 'lucide-react';
export function SlideConfigurator() {
  return <div className="slide-content" data-id="element-135">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} data-id="element-136">
        <h1 className="slide-headline" data-id="element-137">
          Configure your investment.{' '}
          <span className="text-text-muted font-normal" data-id="element-138">Live.</span>
        </h1>
      </motion.div>

      <div className="flex-1 grid grid-cols-2 gap-16 mt-8 items-center" data-id="element-139">
        {/* Left: Configurator Mockup */}
        <motion.div className="bg-card-bg rounded-2xl border border-gray-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden" initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} data-id="element-140">
          {/* Header */}
          <div className="mb-6" data-id="element-141">
            <p className="text-roche-red font-semibold text-sm uppercase tracking-wider mb-1" data-id="element-142">
              Snakker
            </p>
            <h3 className="text-xl font-bold" data-id="element-143">
              Fast Marketing™ Cost Calculator
            </h3>
            <p className="text-sm text-text-muted mt-1" data-id="element-144">
              Estimate your annual campaign operating cost
            </p>
          </div>

          {/* Mockup UI Elements */}
          <div className="space-y-5" data-id="element-145">
            {/* Master Snak Type */}
            <div data-id="element-146">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2" data-id="element-147">
                Master Snak Type
              </p>
              <div className="flex gap-3" data-id="element-148">
                {['Rollout Only', 'Teaser · €2,500', 'Trailer · €5,000'].map((item, i) => <motion.div key={i} className={`flex-1 text-center py-3 px-2 rounded-lg text-sm font-medium border transition-all ${i === 0 ? 'border-roche-red bg-white text-roche-red shadow-sm' : 'border-gray-200 bg-white text-text-muted'}`} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.6 + i * 0.1
              }} data-id="element-149">
                      {item}
                    </motion.div>)}
              </div>
            </div>

            {/* Countries */}
            <div data-id="element-150">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2" data-id="element-151">
                Countries
              </p>
              <div className="flex gap-3" data-id="element-152">
                {['1–4 · €2,000', '5–9 · €1,750', '10+ · €1,500'].map((item, i) => <motion.div key={i} className={`flex-1 text-center py-3 px-2 rounded-lg text-sm font-medium border ${i === 0 ? 'border-roche-red bg-white text-roche-red shadow-sm' : 'border-gray-200 bg-white text-text-muted'}`} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.9 + i * 0.1
              }} data-id="element-153">
                      {item}
                    </motion.div>)}
              </div>
            </div>

            {/* Estimated Cost Output */}
            <motion.div className="bg-white rounded-xl border border-gray-200 p-5 mt-4 flex items-center justify-between" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 1.3,
            duration: 0.5
          }} data-id="element-154">
              <div data-id="element-155">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider" data-id="element-156">
                  Estimated Annual Cost
                </p>
              </div>
              <div className="text-3xl font-bold text-text-main" data-id="element-157">
                2.750{' '}
                <span className="text-lg font-medium text-text-muted" data-id="element-158">
                  €/yr
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Right: Value Props + CTA */}
        <motion.div className="space-y-10" initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} data-id="element-159">
          <div className="space-y-8" data-id="element-160">
            {[{
            icon: Calculator,
            title: 'Transparent pricing',
            body: 'No hidden fees. Configure variables, see the cost instantly.'
          }, {
            icon: Globe,
            title: 'Scales across markets',
            body: 'Adjust brands, campaigns, countries — watch the economics improve.'
          }, {
            icon: BarChart3,
            title: 'Cost per insight included',
            body: 'Monthly behavioral data built into every deployment.'
          }].map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={index} className="flex items-start gap-5" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.8 + index * 0.2
            }} data-id="element-161">
                  <div className="w-12 h-12 rounded-full bg-roche-red/10 flex items-center justify-center flex-shrink-0" data-id="element-162">
                    <Icon className="w-6 h-6 text-roche-red" data-id="element-163" />
                  </div>
                  <div data-id="element-164">
                    <h3 className="text-xl font-bold mb-1" data-id="element-165">{item.title}</h3>
                    <p className="text-text-muted text-lg leading-relaxed" data-id="element-166">
                      {item.body}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* CTA */}
          <motion.a href="https://configurator.snakker.io" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-roche-red text-white font-semibold text-lg px-8 py-4 rounded-xl hover:bg-red-700 transition-colors duration-200 shadow-lg shadow-roche-red/20" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 1.5
        }} data-id="element-167">
            Try the configurator
            <ExternalLink className="w-5 h-5" data-id="element-168" />
          </motion.a>
        </motion.div>
      </div>

      <motion.div className="slide-punchline" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.6,
      delay: 1.8
    }} data-id="element-169">
        No proposal needed — model it yourself.
      </motion.div>
    </div>;
}